/**
 * Generic retry algorithm with exponential backoff. Pure HOF — clients
 * (fetchWithRetry / apiCall) supply their own defaults that reflect their
 * target API's characteristics (Polygon remote vs our backend EC2).
 *
 * @param {Function} fn          (attempt: number) => Promise<T>
 * @param {Object}   opts
 * @param {number}   opts.retries     max retry count (0 = run once, no retry)
 * @param {number}   opts.delayMs     initial wait before first retry (ms)
 * @param {number}   opts.backoff     multiplier per attempt (e.g. 2 → 1s, 2s, 4s)
 * @param {Function} opts.shouldRetry (err, attempt) => boolean. Default: retry all.
 * @returns {Promise<T>} fn's resolved value, or throws fn's last error.
 */
export const withRetry = async (fn, {
  retries = 0,
  delayMs = 1000,
  backoff = 2,
  shouldRetry = () => true,
} = {}) => {
  for (let attempt = 0; ; attempt++) {
    try {
      return await fn(attempt);
    } catch (err) {
      if (attempt >= retries || !shouldRetry(err, attempt)) throw err;
      const wait = delayMs * (backoff ** attempt);
      await new Promise(r => setTimeout(r, wait));
    }
  }
};
